import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Box } from "@mui/system";
import React, { useMemo, useState } from "react";
import useDragScroll from "../utils/useDragScroll";
import { COLORS } from "../assets/theme";
import CustomCheckbox from "./CustomCheckbox";
import {
  Permission,
  PermissionCategory,
  PermissionPage,
} from "../services/RolePermissionService";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";

const HeaderCell = styled(TableCell)(() => ({
  fontWeight: "500",
  borderBottom: "1px solid #E0E0E0",
  textAlign: "center",
}));

interface RolePermissionTableProps {
  permissions: Record<string, string[]>;
  permissionCategories: PermissionCategory[];
  dependencies: Record<string, string[]>;
  roles: string[];
  onPermissionChange: (permissionName: string, roleName: string) => void;
}

interface PermissionRowProps {
  permission: Permission;
  roles: string[];
  permissions: Record<string, string[]>;
  dependencies: Record<string, string[]>;
  getPermissionLabel: (permissionKey: string) => string;
  onPermissionChange: (permissionName: string, roleName: string) => void;
}

const autoGrantedTooltipSx = {
  bgcolor: "#ffffff",
  color: "#333",
  boxShadow: "0 4px 20px rgba(0,0,0,0.10)",
  border: "1px solid #EDE9DA",
  borderRadius: "10px",
  p: 1.25,
  maxWidth: 260,
};

const autoGrantedArrowSx = {
  color: "#ffffff",
  "&::before": { border: "1px solid #EDE9DA" },
};

const AutoGrantedTooltipContent: React.FC<{
  perms: string[];
  getPermissionLabel: (permissionKey: string) => string;
}> = ({ perms, getPermissionLabel }) => (
  <Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
      <AutoFixHighIcon sx={{ fontSize: 13, color: COLORS.GOLD }} />
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: COLORS.GOLD, letterSpacing: 0.3 }}>
        AUTO-GRANTED
      </Typography>
    </Box>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
      {perms.map((p) => (
        <Box key={p} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box
            sx={{
              width: 4,
              height: 4,
              borderRadius: "50%",
              bgcolor: "#C8B96A",
              flexShrink: 0,
            }}
          />
          <Typography sx={{ fontSize: 11, color: "#555", lineHeight: 1.5 }}>
            {getPermissionLabel(p)}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

const PermissionRow: React.FC<PermissionRowProps> = ({
  permission,
  roles,
  permissions,
  dependencies,
  getPermissionLabel,
  onPermissionChange,
}) => {
  const permissionKey = permission.key;
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const hasPermission = (roleName: string) =>
    permissions[roleName]?.includes(permissionKey) ?? false;

  const autoGranted = dependencies[permissionKey] ?? [];

  return (
    <TableRow
      sx={{
        "& .MuiTableCell-root": { padding: { xs: "2px 8px", sm: "2px 16px" } },
      }}
    >
      <TableCell
        style={{
          color: COLORS.BlackBackground,
          fontWeight: "400",
          borderBottom: "1px solid #F0F0F0",
          position: "sticky",
          left: 0,
          zIndex: 1,
          background: "#fff",
          paddingLeft: isSmall ? "28px" : "84px",
          whiteSpace: isSmall ? "normal" : "nowrap",
          ...(isSmall && { minWidth: 90, maxWidth: 118, fontSize: 12 }),
        }}
      >
        {permission.label}
      </TableCell>
      {roles.map((roleName) => {
        const checked = hasPermission(roleName);
        const showTooltip = autoGranted.length > 0;
        return (
          <TableCell key={roleName} style={{ borderBottom: "1px solid #F0F0F0" }}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Tooltip
                title={
                  showTooltip ? (
                    <AutoGrantedTooltipContent
                      perms={autoGranted}
                      getPermissionLabel={getPermissionLabel}
                    />
                  ) : (
                    ""
                  )
                }
                placement="top"
                arrow
                disableHoverListener={!showTooltip}
                slotProps={{
                  tooltip: { sx: autoGrantedTooltipSx },
                  arrow: { sx: autoGrantedArrowSx },
                }}
              >
                <span>
                  <CustomCheckbox
                    checked={checked}
                    onChange={() => onPermissionChange(permissionKey, roleName)}
                    ariaLabel={`Toggle ${roleName} for ${permission.label}`}
                  />
                </span>
              </Tooltip>
            </Box>
          </TableCell>
        );
      })}
    </TableRow>
  );
};

const RolePermissionTable: React.FC<RolePermissionTableProps> = ({
  permissions,
  permissionCategories,
  dependencies,
  roles,
  onPermissionChange,
}) => {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [expandedPages, setExpandedPages] = useState<Set<string>>(new Set());
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const dragScrollRef = useDragScroll<HTMLDivElement>();

  const permissionLabelMap = useMemo(
    () =>
      new Map(
        permissionCategories.flatMap((category) =>
          category.pages.flatMap((page) =>
            page.permissions.map((permission) => [permission.key, permission.label] as const)
          )
        )
      ),
    [permissionCategories]
  );

  const getPermissionLabel = (permissionKey: string) =>
    permissionLabelMap.get(permissionKey) ?? permissionKey.replaceAll("_", " ");

  const toggleCategory = (categoryKey: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryKey)) {
        next.delete(categoryKey);
      } else {
        next.add(categoryKey);
      }
      return next;
    });
  };

  const togglePage = (pageKey: string) => {
    setExpandedPages((prev) => {
      const next = new Set(prev);
      if (next.has(pageKey)) {
        next.delete(pageKey);
      } else {
        next.add(pageKey);
      }
      return next;
    });
  };

  const hasPermission = (roleName: string, permissionName: string) =>
    permissions[roleName]?.includes(permissionName) ?? false;

  const allPermissionNames = permissionCategories.flatMap((cat) =>
    cat.pages.flatMap((p) => p.permissions.map((permission) => permission.key))
  );

  const isAllSelected = (roleName: string) =>
    allPermissionNames.length > 0 &&
    allPermissionNames.every((perm) => hasPermission(roleName, perm));

  const handleSelectAll = (roleName: string) => {
    const allChecked = isAllSelected(roleName);
    allPermissionNames.forEach((perm) => {
      if (hasPermission(roleName, perm) === allChecked) {
        onPermissionChange(perm, roleName);
      }
    });
  };

  const getCategoryPermissions = (category: PermissionCategory) =>
    category.pages.flatMap((p) => p.permissions.map((permission) => permission.key));

  const isCategoryAllSelected = (roleName: string, category: PermissionCategory) => {
    const perms = getCategoryPermissions(category);
    return perms.length > 0 && perms.every((perm) => hasPermission(roleName, perm));
  };

  const isCategoryIndeterminate = (roleName: string, category: PermissionCategory) => {
    const perms = getCategoryPermissions(category);
    const someSelected = perms.some((perm) => hasPermission(roleName, perm));
    const allSelected = perms.every((perm) => hasPermission(roleName, perm));
    return someSelected && !allSelected;
  };

  const handleCategoryToggle = (
    e: React.ChangeEvent<HTMLInputElement>,
    roleName: string,
    category: PermissionCategory
  ) => {
    e.stopPropagation();
    const allChecked = isCategoryAllSelected(roleName, category);
    getCategoryPermissions(category).forEach((perm) => {
      if (hasPermission(roleName, perm) === allChecked) {
        onPermissionChange(perm, roleName);
      }
    });
  };

  const isPageAllSelected = (roleName: string, page: PermissionPage) =>
    page.permissions.length > 0 &&
    page.permissions.every((perm) => hasPermission(roleName, perm.key));

  const isPageIndeterminate = (roleName: string, page: PermissionPage) => {
    const someSelected = page.permissions.some((perm) => hasPermission(roleName, perm.key));
    const allSelected = isPageAllSelected(roleName, page);
    return someSelected && !allSelected;
  };

  const handlePageToggle = (
    e: React.ChangeEvent<HTMLInputElement>,
    roleName: string,
    page: PermissionPage
  ) => {
    e.stopPropagation();
    const allChecked = isPageAllSelected(roleName, page);
    page.permissions.forEach((perm) => {
      if (hasPermission(roleName, perm.key) === allChecked) {
        onPermissionChange(perm.key, roleName);
      }
    });
  };

  return (
    <Box
      ref={dragScrollRef}
      sx={{
        width: "100%",
        overflowX: "auto",
        overflowY: "auto",
        maxHeight: { xs: "calc(100vh - 160px)", sm: "calc(100vh - 280px)" },
        "@supports (height: 100dvh)": {
          maxHeight: {
            xs: "calc(100dvh - 160px)",
            sm: "calc(100dvh - 280px)",
          },
        },
        "& .MuiCheckbox-root": { p: { xs: 0.5, sm: 1 } },
      }}
    >
      <Table sx={{ minWidth: 600 }} stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell
              style={{
                fontWeight: "500",
                fontSize: isSmall ? 13 : 15,
                color: COLORS.GOLD,
                borderBottom: "2px solid #E0E0E0",
                textAlign: "left",
                position: "sticky",
                left: 0,
                zIndex: 2,
                background: "#fff",
                minWidth: isSmall ? 90 : 260,
                whiteSpace: "nowrap",
              }}
            >
              Permissions
            </TableCell>
            <TableCell
              colSpan={roles.length}
              style={{
                fontWeight: "500",
                fontSize: isSmall ? 13 : 15,
                paddingLeft: isSmall ? "16px" : "74px",
                color: COLORS.GOLD,
                borderBottom: "2px solid #E0E0E0",
                background: "#fff",
                zIndex: 1,
              }}
            >
              Roles
            </TableCell>
          </TableRow>
          <TableRow sx={{ background: "#fff", width: "100%" }}>
            <TableCell
              style={{ position: "sticky", left: 0, zIndex: 2, background: "#fff" }}
            />
            {roles.map((role) => (
              <HeaderCell
                key={role}
                style={{
                  background: "#fff",
                  zIndex: 1,
                  ...(isSmall && { padding: "4px 6px" }),
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <span style={isSmall ? { fontSize: 11 } : undefined}>
                    {role.replaceAll("_", " ")}
                  </span>
                  <span style={{ marginTop: "5px" }}>
                    <CustomCheckbox
                      checked={isAllSelected(role)}
                      onChange={() => handleSelectAll(role)}
                      ariaLabel={`Select all permissions for ${role}`}
                    />
                  </span>
                </div>
              </HeaderCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {permissionCategories.map((category) => (
            <React.Fragment key={category.category}>
              {/* Category row */}
              <TableRow
                onClick={() => toggleCategory(category.category)}
                sx={{
                  cursor: "pointer",
                  backgroundColor: "#f7f5ef",
                  "&:hover": { backgroundColor: "#f0ede3" },
                  "& .MuiTableCell-root": {
                    padding: { xs: "4px 8px", sm: "4px 16px" },
                  },
                }}
              >
                <TableCell
                  style={{
                    position: "sticky",
                    left: 0,
                    zIndex: 1,
                    background: "inherit",
                    borderBottom: "1px solid #E0E0E0",
                    fontWeight: 600,
                    color: COLORS.BlackBackground,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <IconButton size="small" tabIndex={-1} sx={{ pointerEvents: "none" }}>
                      {expandedCategories.has(category.category) ? (
                        <KeyboardArrowDownIcon fontSize="small" sx={{ color: COLORS.GOLD }} />
                      ) : (
                        <KeyboardArrowRightIcon fontSize="small" sx={{ color: COLORS.GOLD }} />
                      )}
                    </IconButton>
                    <span>{category.category.replaceAll("_", " ")}</span>
                  </Box>
                </TableCell>
                {roles.map((roleName) => (
                  <TableCell
                    key={roleName}
                    style={{ textAlign: "center", borderBottom: "1px solid #E0E0E0" }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <CustomCheckbox
                        checked={isCategoryAllSelected(roleName, category)}
                        indeterminate={isCategoryIndeterminate(roleName, category)}
                        onChange={(e) => handleCategoryToggle(e, roleName, category)}
                        ariaLabel={`Toggle all ${category.category} permissions for ${roleName}`}
                      />
                    </Box>
                  </TableCell>
                ))}
              </TableRow>

              {/* Page rows */}
              {expandedCategories.has(category.category) &&
                category.pages.map((page) => {
                  const pageKey = `${category.category}::${page.page}`;
                  return (
                    <React.Fragment key={pageKey}>
                      <TableRow
                        onClick={() => togglePage(pageKey)}
                        sx={{
                          cursor: "pointer",
                          backgroundColor: "#faf9f5",
                          "&:hover": { backgroundColor: "#f3f1e8" },
                          "& .MuiTableCell-root": {
                            padding: { xs: "3px 8px", sm: "3px 16px" },
                          },
                        }}
                      >
                        <TableCell
                          style={{
                            position: "sticky",
                            left: 0,
                            zIndex: 1,
                            background: "inherit",
                            borderBottom: "1px solid #EBEBEB",
                            fontWeight: 500,
                            color: COLORS.BlackBackground,
                            paddingLeft: isSmall ? "20px" : "32px",
                            ...(isSmall && { fontSize: 13 }),
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <IconButton size="small" tabIndex={-1} sx={{ pointerEvents: "none" }}>
                              {expandedPages.has(pageKey) ? (
                                <KeyboardArrowDownIcon
                                  fontSize="small"
                                  sx={{ color: COLORS.GOLD, opacity: 0.7 }}
                                />
                              ) : (
                                <KeyboardArrowRightIcon
                                  fontSize="small"
                                  sx={{ color: COLORS.GOLD, opacity: 0.7 }}
                                />
                              )}
                            </IconButton>
                            <span>{page.page.replaceAll("_", " ")}</span>
                          </Box>
                        </TableCell>
                        {roles.map((roleName) => (
                          <TableCell
                            key={roleName}
                            style={{ textAlign: "center", borderBottom: "1px solid #EBEBEB" }}
                          >
                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                              <CustomCheckbox
                                checked={isPageAllSelected(roleName, page)}
                                indeterminate={isPageIndeterminate(roleName, page)}
                                onChange={(e) => handlePageToggle(e, roleName, page)}
                                ariaLabel={`Toggle all ${page.page} permissions for ${roleName}`}
                              />
                            </Box>
                          </TableCell>
                        ))}
                      </TableRow>

                      {/* Permission rows */}
                      {expandedPages.has(pageKey) &&
                        page.permissions.map((permission) => (
                          <PermissionRow
                            key={permission.key}
                            permission={permission}
                            roles={roles}
                            permissions={permissions}
                            dependencies={dependencies}
                            getPermissionLabel={getPermissionLabel}
                            onPermissionChange={onPermissionChange}
                          />
                        ))}
                    </React.Fragment>
                  );
                })}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default RolePermissionTable;
